/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';

import { sanitizeHTML } from 'libs/string';
import { clearTimeoutIfExists } from 'libs/node';

import { useIntersection } from 'hooks/useIntersection';

import type { Cache, Props } from './Icon.types';

const regexWithViewBox = /<svg\s[^>]*\bviewBox="([^"]*)"[^>]*>([\s\S]*?)<\/svg>/;
const regexWithoutViewBox = /<svg\s[^>]*>([\s\S]*?)<\/svg>/;

let cache: Cache = {};
let clearCacheTimer: NodeJS.Timeout;
const clearCache = () => {
  cache = {};
};

const request = async (url: string) => {
  clearTimeoutIfExists(clearCacheTimer);
  clearCacheTimer = setTimeout(clearCache, 1000 * 60);

  const cachedData = cache?.[url]?.data;
  if (cachedData) {
    return cachedData;
  }

  return new Promise((resolve, reject) => {
    if (url in cache === false) {
      cache[url] = {};
    }

    const cached = cache[url];

    if (cached && cached.pending && cached.promise) {
      return cached.promise.then(resolve).catch(reject);
    }

    cached.pending = true;
    cached.promise = fetch(url)
      .then((response) => {
        cached.pending = false;

        const contentType = response.headers.get('content-type');
        const [fileType] = (contentType ?? '').split(/ ?; ?/);

        if (response.status > 299 || !['image/svg+xml', 'text/plain'].some((d) => fileType.includes(d))) {
          throw new Error('Unexpected error occurred.');
        }

        const text = response.text();
        cached.data = text;

        return text;
      })
      .catch((err) => {
        cached.pending = false;
        return Promise.reject(err);
      });

    return cached.promise?.then(resolve).catch(reject);
  });
};

export const Icon = ({
  disabled,
  style,
  className,
  type = '',
  size = 'small',
  onClick,
  url,
  hoverable = false,
  preserveAspectRatio,
}: Props) => {
  const ref = useRef<SVGSVGElement>(null);
  const intersected = useIntersection(ref);

  const [state, setState] = useState({
    hasStroke: false,
    viewBox: '',
    path: '',
  });

  useEffect(() => {
    if (intersected === false) {
      return;
    }

    (async () => {
      try {
        const svgString = await request(url || `/storage/svg/base/${type}.svg`);

        if (typeof svgString !== 'string') {
          return;
        }

        let [, viewBox, path] = regexWithViewBox.exec(sanitizeHTML(svgString)) || [];

        if (!viewBox || !path) {
          viewBox = '0 0 16 16';
          [, path] = regexWithoutViewBox.exec(sanitizeHTML(svgString)) || [];
        }

        if (!path) {
          throw new Error('SVG data extraction failed.');
        }

        // Generate unique ID prefix to avoid conflicts when multiple SVGs are used
        const uniqueId = `${type}-${Math.random().toString(36).substr(2, 9)}`;
        path = path.replace(/id="([^"]+)"/g, `id="${uniqueId}-$1"`).replace(/url\(#([^)]+)\)/g, `url(#${uniqueId}-$1)`);

        setState({ hasStroke: svgString.includes('stroke='), viewBox, path });
      } catch (error) {
        // Missing icon file (404) shouldn't spam the console as a hard error — it
        // just renders an empty svg. Keep a quiet warn so it's still discoverable.
        // eslint-disable-next-line no-console
        console.warn(`Icon failed to load: ${url || type}`, error);
      }
    })();
  }, [type, intersected, url]);

  const defaultProps = useMemo(
    () => ({
      width: '1em',
      height: '1em',
      style,
      className: cn(
        'gdb-icon',
        `gdb-icon-size-${size}`,
        state.hasStroke && 'gdb-icon-stroked',
        disabled && 'disabled',
        type && `gdb-${type} gdb-icon-${type}`,
        hoverable && 'icon-hoverable',
        className,
      ),
      onClick,
      fill: state.hasStroke ? 'none' : undefined,
    }),
    [state.hasStroke, type, size, className, style, disabled, onClick, hoverable],
  );

  if (!type && !url) {
    return null;
  }

  return (
    <svg
      ref={ref}
      viewBox={state.viewBox || undefined}
      preserveAspectRatio={preserveAspectRatio}
      dangerouslySetInnerHTML={{ __html: state.path }}
      {...defaultProps}
    />
  );
};
